// Global state to track which component is active
let activeComponent = null;

// Z-index constants for popups and overlays
const Z_INDEX = {
  SEARCH_POPUP: 1010, // Above overlay (1001)
  OVERLAY_SEARCH: 1001, // Above header
  CART_DRAWER: 1020,
  OVERLAY_CART_DRAWER: 1015,
  MOBILE_MENU_DRAWER: 1002,
  MOBILE_MENU_OVERLAY: 1001,
  DEFAULT_OVERLAY: 999, // Base overlay z-index
};

// Overlay z-index management
const setOverlayZIndex = (zIndex) => {
  const mainOverlay = document.querySelector(".main__overlay");
  if (mainOverlay) {
    mainOverlay.style.zIndex = zIndex;
  }
};

// Reset overlay z-index to default
const resetOverlayZIndex = () => {
  const mainOverlay = document.querySelector(".main__overlay");
  if (mainOverlay) {
    mainOverlay.style.zIndex = Z_INDEX.DEFAULT_OVERLAY;
  }
};

// Helper function to close other popups
const closeOtherPopups = (excludeComponent) => {
  if (activeComponent === "search" && excludeComponent !== "search") {
    closeSearch();
  }
  if (activeComponent === "cart" && excludeComponent !== "cart") {
    closeCartDrawer();
  }
  if (
    activeComponent === "request-call" &&
    excludeComponent !== "request-call"
  ) {
    closeRequestCallPopup();
  }
  if (activeComponent === "fabric-help" && excludeComponent !== "fabric-help") {
    closeFabricHelp();
  }
  if (activeComponent === "menu" && excludeComponent !== "menu") {
    toggleMenu();
  }
  if (
    activeComponent === "localization" &&
    excludeComponent !== "localization"
  ) {
    if (document.body.classList.contains("has-localization")) {
      closeLocalization("close");
    }
  }
};

// ============================================================================
// SEARCH POPUP (Header Search)
// ============================================================================

const openSearch = () => {
  const searchForm = document.getElementById("header__search");
  const searchInput = searchForm?.querySelector(".header__search-input");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!searchForm || searchForm.classList.contains("active")) return;

  // Close other popups
  closeOtherPopups("search");

  // Lock header
  if (window.HeaderControl) {
    HeaderControl.lock();
  }

  // Prepare search UI
  searchForm.style.transition = "all 0.3s ease";

  if (window.lenis) {
    window.lenis.stop();
  }
  document.documentElement.classList.add("scroll-locked");

  // Set overlay z-index for search
  setOverlayZIndex(Z_INDEX.OVERLAY_SEARCH);

  // Adjust overlay position (below header)
  if (window.adjustOverlay) {
    adjustOverlay();
  }

  // Use requestAnimationFrame for smoother animation
  requestAnimationFrame(() => {
    searchForm.classList.add("active");
    mainOverlay.classList.add("active");
    if (searchInput) {
      searchInput.focus();
    }
    activeComponent = "search";

    // Kaleido: don't close on overlay click - only close button / Escape
    const isKaleido = searchForm.classList.contains("header__search-kaleido");
    if (!isKaleido) {
      const overlayClickHandler = (e) => {
        if (e.target === mainOverlay) {
          closeSearch(false);
        }
      };
      mainOverlay.addEventListener("click", overlayClickHandler);
      searchForm._overlayHandler = overlayClickHandler;
    }

    // Kaleido: show recommendations on open
    if (isKaleido) {
      const ps = searchForm.querySelector("predictive-search-kaleido");
      if (ps && typeof ps.showRecommendations === "function") {
        ps.showRecommendations();
      }
    }
  });
};

const closeSearch = (mouseInHeader = false) => {
  const searchForm = document.getElementById("header__search");
  const searchInput = searchForm?.querySelector(".header__search-input");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!searchForm || !searchForm.classList.contains("active")) return;

  // Reset search UI
  searchForm.style.transition = "none";
  searchForm.classList.remove("active");
  mainOverlay.classList.remove("active");

  if (searchInput) {
    searchInput.value = "";
  }

  // Reset overlay position
  if (window.resetOverlay) {
    resetOverlay();
  }

  // Reset overlay z-index
  resetOverlayZIndex();

  if (window.lenis) {
    window.lenis.start();
  }
  document.documentElement.classList.remove("scroll-locked");

  // Remove overlay click handler
  if (searchForm._overlayHandler) {
    mainOverlay.removeEventListener("click", searchForm._overlayHandler);
    searchForm._overlayHandler = null;
  }

  // Unlock and restore header state
  if (window.HeaderControl) {
    HeaderControl.unlock();
  }

  activeComponent = null;
};

// ============================================================================
// MOBILE MENU
// ============================================================================

const toggleMenu = () => {
  const mobileMenuBtn = document.getElementById("mobile-menu-btn");
  const mobileMenuDropdown = document.getElementById("mobile-menu-dropdown");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!mobileMenuBtn || !mobileMenuDropdown) return;

  const isOpening = !mobileMenuBtn.classList.contains("active");

  mobileMenuBtn.classList.toggle("active");
  mobileMenuDropdown.classList.toggle("active");

  if (isOpening) {
    // Lock header
    if (window.HeaderControl) {
      HeaderControl.lock();
    }

    if (window.lenis) {
      window.lenis.stop();
    }
    document.documentElement.classList.add("scroll-locked");

    // Close other popups
    closeOtherPopups("menu");

    // Set overlay z-index for mobile menu
    setOverlayZIndex(Z_INDEX.MOBILE_MENU_OVERLAY);

    // Add overlay click handler when opening
    const overlayClickHandler = (e) => {
      if (e.target === mainOverlay) {
        toggleMenu();
      }
    };
    mainOverlay.addEventListener("click", overlayClickHandler);
    mobileMenuDropdown._overlayHandler = overlayClickHandler;

    // Set active component
    activeComponent = "menu";
  } else {
    // Unlock header
    if (window.HeaderControl) {
      HeaderControl.unlock();
    }

    if (window.lenis) {
      window.lenis.start();
    }
    document.documentElement.classList.remove("scroll-locked");

    // Remove overlay click handler when closing
    if (mobileMenuDropdown._overlayHandler) {
      mainOverlay.removeEventListener(
        "click",
        mobileMenuDropdown._overlayHandler
      );
      mobileMenuDropdown._overlayHandler = null;
    }

    // Reset overlay z-index
    resetOverlayZIndex();

    activeComponent = null;
  }

  // Toggle overlay
  if (mainOverlay.classList.contains("active")) {
    mainOverlay.classList.remove("active");
  } else {
    mainOverlay.classList.add("active");
  }
};

// ============================================================================
// CART DRAWER
// ============================================================================

const openCartDrawer = () => {
  const cartDrawer = document.getElementById("CartDrawer");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!cartDrawer || cartDrawer.classList.contains("open")) return;

  // Close other popups
  closeOtherPopups("cart");

  cartDrawer.classList.add("open");
  mainOverlay.classList.add("active");

  // Set overlay z-index for cart drawer
  setOverlayZIndex(Z_INDEX.OVERLAY_CART_DRAWER);

  activeComponent = "cart";

  if (window.lenis) {
    window.lenis.stop();
  }
  document.documentElement.classList.add("scroll-locked");

  // Lock header
  if (window.HeaderControl) {
    HeaderControl.lock();
  }

  // Add overlay click handler
  const overlayClickHandler = (e) => {
    if (e.target === mainOverlay) {
      closeCartDrawer();
    }
  };
  mainOverlay.addEventListener("click", overlayClickHandler);
  cartDrawer._overlayHandler = overlayClickHandler;
};

const closeCartDrawer = () => {
  const cartDrawer = document.getElementById("CartDrawer");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!cartDrawer || !cartDrawer.classList.contains("open")) return;

  cartDrawer.classList.remove("open");
  mainOverlay.classList.remove("active");

  // Reset overlay z-index
  resetOverlayZIndex();

  if (window.lenis) {
    window.lenis.start();
  }
  document.documentElement.classList.remove("scroll-locked");
  activeComponent = null;

  // Remove overlay click handler
  if (cartDrawer._overlayHandler) {
    mainOverlay.removeEventListener("click", cartDrawer._overlayHandler);
    cartDrawer._overlayHandler = null;
  }

  // Unlock and restore header state
  if (window.HeaderControl) {
    HeaderControl.unlock();
  }
};

// ============================================================================
// FABRIC HELP (Can't Decide) POPUP - works on product page and quick-add
// ============================================================================

const openFabricHelp = () => {
  const overlay = document.querySelector("[data-fabric-help-overlay]");
  if (!overlay || overlay.classList.contains("is-open")) return;

  closeOtherPopups("fabric-help");

  overlay.classList.add("is-open");
  overlay.setAttribute("aria-hidden", "false");
  document.body.style.overflow = "hidden";
  if (window.lenis && typeof window.lenis.stop === "function")
    window.lenis.stop();

  const popup = overlay.querySelector(".fabric-help-popup");
  if (popup && typeof gsap !== "undefined" && gsap.to) {
    gsap.fromTo(
      popup,
      { opacity: 0, scale: 0.96 },
      { opacity: 1, scale: 1, duration: 0.25, ease: "power2.out" }
    );
  }
  activeComponent = "fabric-help";
};

const closeFabricHelp = () => {
  const overlay = document.querySelector("[data-fabric-help-overlay]");
  if (!overlay || !overlay.classList.contains("is-open")) return;

  const popup = overlay.querySelector(".fabric-help-popup");
  if (popup && typeof gsap !== "undefined" && gsap.to) {
    gsap.to(popup, {
      opacity: 0,
      scale: 0.96,
      duration: 0.2,
      ease: "power2.in",
      onComplete: () => {
        overlay.classList.remove("is-open");
        overlay.setAttribute("aria-hidden", "true");
        document.body.style.overflow = "";
        if (window.lenis && typeof window.lenis.start === "function")
          window.lenis.start();
        activeComponent = null;
      },
    });
  } else {
    overlay.classList.remove("is-open");
    overlay.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";
    if (window.lenis && typeof window.lenis.start === "function")
      window.lenis.start();
    activeComponent = null;
  }
};

// ============================================================================
// REQUEST CALL POPUP (form + cart items, works on all pages)
// ============================================================================

const openRequestCallPopup = () => {
  const overlay = document.querySelector("[data-request-call-overlay]");
  if (!overlay || overlay.classList.contains("is-open")) return;

  closeOtherPopups("request-call");

  overlay.classList.add("is-open");
  overlay.setAttribute("aria-hidden", "false");
  document.body.style.overflow = "hidden";
  if (window.lenis && typeof window.lenis.stop === "function")
    window.lenis.stop();

  if (typeof window.renderRequestCallCartItems === "function") {
    window.renderRequestCallCartItems();
  }
  const rcPopup = overlay.querySelector(".request-call-popup");
  if (rcPopup && typeof gsap !== "undefined" && gsap.to) {
    gsap.fromTo(
      rcPopup,
      { opacity: 0, scale: 0.96 },
      { opacity: 1, scale: 1, duration: 0.25, ease: "power2.out" }
    );
  }
  activeComponent = "request-call";
};

const closeRequestCallPopup = () => {
  const overlay = document.querySelector("[data-request-call-overlay]");
  if (!overlay || !overlay.classList.contains("is-open")) return;

  overlay.classList.remove("is-open");
  overlay.setAttribute("aria-hidden", "true");
  document.body.style.overflow = "";
  if (window.lenis && typeof window.lenis.start === "function")
    window.lenis.start();
  activeComponent = null;
};

function buildCartItemsText(items) {
  if (!items || items.length === 0) return "";
  return items
    .map((item) => {
      let variantLabel = "";
      if (item.variant_title && item.variant_title !== "Default Title")
        variantLabel = item.variant_title;
      else if (item.options_with_values && item.options_with_values.length)
        variantLabel = item.options_with_values.map((o) => o.value).join(" / ");
      const title = (item.product_title || item.title || "").trim();
      return variantLabel ? title + " - " + variantLabel : title;
    })
    .join(" , ");
}

function updateRequestCallCartItemsField(text) {
  const field = document.querySelector("[data-request-call-cart-items-field]");
  if (field) field.value = text || "";
}

function renderRequestCallCartItems() {
  const container = document.querySelector("[data-request-call-cart-items]");
  const row = document.querySelector("[data-request-call-cart-row]");
  if (!container || !row) return;
  fetch("/cart.js")
    .then((r) => r.json())
    .then((cart) => {
      const items = cart && cart.items ? cart.items : [];
      const text = buildCartItemsText(items);
      updateRequestCallCartItemsField(text);
      if (items.length === 0) {
        row.style.display = "none";
        return;
      }
      row.style.display = "";
      container.innerHTML = "";
      items.forEach((item) => {
        let variantLabel = "";
        if (item.variant_title && item.variant_title !== "Default Title")
          variantLabel = item.variant_title;
        else if (item.options_with_values && item.options_with_values.length)
          variantLabel = item.options_with_values
            .map((o) => o.value)
            .join(" / ");
        const card = document.createElement("div");
        card.className = "request-call-cart-item";
        card.dataset.lineKey = item.key;
        card.innerHTML =
          '<div class="request-call-cart-item__image">' +
          (item.image
            ? '<img src="' +
              item.image.replace(/&amp;/g, "&") +
              '" alt="" width="48" height="48" loading="lazy">'
            : '<span class="request-call-cart-item__no-image"></span>') +
          '</div><div class="request-call-cart-item__info">' +
          '<span class="request-call-cart-item__title">' +
          (item.product_title || item.title) +
          (variantLabel ? " – " + variantLabel : "") +
          "</span></div>" +
          '<button type="button" class="request-call-cart-item__remove" aria-label="Remove from list" data-request-call-cart-remove></button>';
        container.appendChild(card);
      });
      container
        .querySelectorAll("[data-request-call-cart-remove]")
        .forEach((btn) => {
          btn.addEventListener("click", function () {
            const card = btn.closest(".request-call-cart-item");
            if (!card || card.classList.contains("is-loading")) return;
            const key = card.dataset.lineKey;
            if (!key) return;
            card.classList.add("is-loading");
            fetch("/cart/change.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: key, quantity: 0 }),
            })
              .then((r) => {
                if (!r.ok)
                  return r.json().then((err) => {
                    throw err;
                  });
                return r.json();
              })
              .then(() => {
                // Re-render list so remaining items get fresh line keys (Shopify can change keys after remove)
                renderRequestCallCartItems();
              })
              .catch(() => {
                card.classList.remove("is-loading");
              });
          });
        });
    });
}

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener("DOMContentLoaded", () => {
  const header = document.getElementById("main-header");

  // Fabric help (Can't Decide) popup
  window.openFabricHelp = openFabricHelp;
  window.closeFabricHelp = closeFabricHelp;
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-fabric-help-open]");
    if (btn) {
      e.preventDefault();
      openFabricHelp();
    }
  });
  document.querySelectorAll("[data-fabric-help-close]").forEach((el) => {
    el.addEventListener("click", closeFabricHelp);
  });
  document
    .querySelectorAll("[data-fabric-help-to-request-call]")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        closeFabricHelp();
        setTimeout(() => {
          if (typeof window.openRequestCallPopup === "function")
            window.openRequestCallPopup();
        }, 220);
      });
    });

  // Request call popup: expose helpers and bind [data-request-call]
  window.renderRequestCallCartItems = renderRequestCallCartItems;
  window.openRequestCallPopup = openRequestCallPopup;
  window.closeRequestCallPopup = closeRequestCallPopup;

  document.querySelectorAll("[data-request-call]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      openRequestCallPopup();
    });
  });

  const requestCallOverlay = document.querySelector(
    "[data-request-call-overlay]"
  );
  if (requestCallOverlay) {
    requestCallOverlay
      .querySelectorAll("[data-request-call-close]")
      .forEach((el) => {
        el.addEventListener("click", closeRequestCallPopup);
      });
    // Contact form submit
    const contactForm = document.querySelector("form.request-call-form");
    if (contactForm) {
      contactForm.addEventListener("submit", function (e) {
        e.preventDefault();
        const propertyCheckboxes = contactForm.querySelectorAll(
          ".request-call-checkbox:checked"
        );
        const propertyError = contactForm.querySelector(
          ".request-call-error--property"
        );
        if (propertyCheckboxes.length === 0) {
          if (propertyError) propertyError.style.display = "block";
          return;
        }
        if (propertyError) propertyError.style.display = "none";

        let bodyText =
          "Property type: " +
          Array.from(propertyCheckboxes)
            .map((cb) => cb.value)
            .join(", ");
        const timeInput = contactForm.querySelector("[data-request-call-time]");
        const timeVal = timeInput ? timeInput.value.trim() : "";
        if (timeVal) bodyText += "\nPreferred time: " + timeVal;
        if (typeof window.getRequestCallBodyProductLine === "function") {
          const line = window.getRequestCallBodyProductLine();
          if (line) bodyText += "\n" + line;
        }
        const bodyInput = contactForm.querySelector("[data-request-call-body]");
        if (bodyInput) bodyInput.value = bodyText;

        const submitBtn = contactForm.querySelector(".request-call-submit");
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = "Sending...";
        }
        const formData = new FormData(contactForm);
        fetch(contactForm.action, { method: "POST", body: formData })
          .then(() => {
            const productUrl =
              typeof window.productRequestCallUrl !== "undefined"
                ? window.productRequestCallUrl
                : window.location.pathname + window.location.search;
            const sep = productUrl.indexOf("?") !== -1 ? "&" : "?";
            window.location.href = productUrl + sep + "request_sent=1";
          })
          .catch(() => {
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = "REQUEST A FREE CALL";
            }
          });
      });
    }
  }

  // Mobile menu functionality

  // Mobile menu functionality
  const mobileMenuBtn = document.getElementById("mobile-menu-btn");
  if (mobileMenuBtn) {
    mobileMenuBtn.addEventListener("click", toggleMenu);
  }

  // Search functionality
  const searchBtns = document.querySelectorAll(".search-btn");
  searchBtns.forEach((searchBtn) => {
    if (searchBtn) {
      searchBtn.addEventListener("click", () => {
        if (activeComponent === "search") {
          closeSearch();
        } else {
          openSearch();
        }
      });
    }
  });

  // Close search when clicking outside (header/announcement)
  document.addEventListener("mousedown", (e) => {
    if (activeComponent !== "search") return;

    const searchForm = document.getElementById("header__search");

    const searchElements = [
      document.querySelector(".header__search-container"),
      ...document.querySelectorAll(".search-btn"),
      searchForm,
      // Kaleido: entire popup is "inside" - never close on click inside
      document.querySelector(".search-kaleido-popup"),
      document.querySelector(".predictive-search-kaleido-wrap"),
    ].filter(Boolean);

    const clickedInsideSearch = searchElements.some(
      (el) => el === e.target || el.contains(e.target)
    );

    if (!clickedInsideSearch) {
      closeSearch(header?.contains(e.target));
    }
  });

  // Escape key handler
  const handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      if (activeComponent === "menu") {
        toggleMenu();
      } else if (activeComponent === "search") {
        const isMouseOverHeaderNow = header?.matches(":hover");
        closeSearch(isMouseOverHeaderNow);
      } else if (activeComponent === "cart") {
        closeCartDrawer();
      } else if (activeComponent === "request-call") {
        closeRequestCallPopup();
      } else if (activeComponent === "fabric-help") {
        closeFabricHelp();
      } else if (activeComponent === "video-popup") {
        if (window.closeVideoPopup) {
          window.closeVideoPopup();
        }
      } else if (activeComponent === "filter") {
        if (window.closeFilterDrawer) {
          window.closeFilterDrawer();
        }
      } else if (activeComponent === "localization") {
        closeLocalization("close");
      }
    }
  };

  // Add escape key listener
  document.addEventListener("keydown", handleEscapeKey);

  document.querySelectorAll(".localization-button").forEach((btn) => {
    btn.addEventListener("click", openLocalization);
  });

  // Make functions available globally
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;
  window.openSearch = openSearch;
  window.closeSearch = closeSearch;
  window.toggleMenu = toggleMenu;
  window.activeComponent = activeComponent;

  // Export overlay management functions
  window.setOverlayZIndex = setOverlayZIndex;
  window.resetOverlayZIndex = resetOverlayZIndex;
  window.Z_INDEX = Z_INDEX;
});

// ============================================================================
// HELPER FUNCTIONS (for other popups)
// ============================================================================

const closeAllPopups = (excludeComponent) => {
  if (excludeComponent !== "search") {
    closeSearch();
  }
  if (excludeComponent !== "cart") {
    closeCartDrawer();
  }
  if (excludeComponent !== "request-call") {
    closeRequestCallPopup();
  }
  if (excludeComponent !== "fabric-help") {
    closeFabricHelp();
  }
  if (excludeComponent !== "menu") {
    const mobileMenuBtn = document.getElementById("mobile-menu-btn");
    if (mobileMenuBtn?.classList.contains("active")) {
      toggleMenu();
    }
  }
  if (excludeComponent !== "video-popup" && window.closeVideoPopup) {
    window.closeVideoPopup();
  }
  if (excludeComponent !== "filter" && window.closeFilterDrawer) {
    window.closeFilterDrawer();
  }
  if (
    excludeComponent !== "localization" &&
    document.body.classList.contains("has-localization")
  ) {
    closeLocalization("close");
  }
};

// Legacy support for filter drawer (if exists elsewhere)
if (typeof window.openFilterDrawer === "undefined") {
  window.openFilterDrawer = () => {
    const filterDrawer = document.querySelector(".filters-drawer");
    const overlay = document.querySelector(".main__overlay");
    if (!filterDrawer) return;

    closeOtherPopups("filter");

    filterDrawer.classList.add("open");
    document.body.style.overflow = "hidden";
    if (overlay) {
      overlay.classList.add("active");
      const overlayClickHandler = (e) => {
        if (e.target === overlay) {
          window.closeFilterDrawer();
        }
      };
      overlay.addEventListener("click", overlayClickHandler);
      filterDrawer._overlayHandler = overlayClickHandler;
    }
    activeComponent = "filter";

    if (window.HeaderControl) {
      HeaderControl.lock();
    }
  };
}

if (typeof window.closeFilterDrawer === "undefined") {
  window.closeFilterDrawer = () => {
    const filterDrawer = document.querySelector(".filters-drawer");
    const overlay = document.querySelector(".main__overlay");
    if (!filterDrawer) return;

    filterDrawer.classList.remove("open");
    document.body.style.overflow = "";
    if (overlay) {
      overlay.classList.remove("active");
      if (filterDrawer._overlayHandler) {
        overlay.removeEventListener("click", filterDrawer._overlayHandler);
        filterDrawer._overlayHandler = null;
      }
    }
    resetOverlayZIndex();
    activeComponent = null;

    if (window.HeaderControl) {
      HeaderControl.unlock();
    }
  };
}

// ============================================================================
// LOCALIZATION
// ============================================================================

const LOCALIZATION_TIMER_KEY = "localizationTimer";
const LOCALIZATION_TIMER_DURATION = 360000; // 1 hour in milliseconds
let localizationId;

// Open localization menu
const openLocalization = () => {
  clearTimeout(localizationId);
  const localizationMenu = document.getElementById("localization-menu");
  if (!localizationMenu) {
    console.log("localizationMenu not found in open");
    return;
  }

  if (document.body.classList.contains("has-localization")) {
    closeLocalization();
  } else {
    // Reset the timer whenever manually opened
    localStorage.removeItem(LOCALIZATION_TIMER_KEY);

    activeComponent = "localization";
    closeAllPopups("localization");
    localizationMenu.classList.remove("hiding");
    document.body.classList.add("has-localization");

    // Update button state
    const localizationButton = document.getElementById("localization-button");
    if (localizationButton) {
      localizationButton.setAttribute("aria-expanded", "true");
    }

    localizationMenu.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  }
};

// Close localization menu
const closeLocalization = (type) => {
  const localizationMenu = document.getElementById("localization-menu");
  if (!localizationMenu) {
    console.log("localizationMenu not found in close");
    return;
  }

  // Start the timer when closed
  localStorage.setItem(LOCALIZATION_TIMER_KEY, Date.now().toString());

  const localizationButton = document.getElementById("localization-button");
  if (localizationButton) {
    localizationButton.setAttribute("aria-expanded", "false");
  }

  if (type === "close") {
    localizationMenu.classList.add("hiding");
  }

  localizationId = setTimeout(() => {
    document.body.classList.remove("has-localization");
  }, 300);

  activeComponent = null;
};

// Make functions available globally
window.openLocalization = openLocalization;
window.closeLocalization = closeLocalization;
